import { type IGetDashboardFilters, buildMakeUseAxios } from "@api";
import type { AdditionalFiltersFormatted, ICandidate } from "@bbj-types/candidate";
import type { ICandidatesFiltersBody } from "@bbj-types/offer/details";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";

const apiAxios = buildMakeUseAxios(`${import.meta.env.VITE_CANDIDATE_SERVICE_URL}`);

export type TGetCandidate = ICandidate & {
  beneficiaryLastConnection?: Date;
};

export const useGetCandidate = () => {
  const [state, execute, cancel] = apiAxios<TGetCandidate, void, unknown>(
    {
      url: "/candidates",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId: string) => {
      return execute({
        url: `/candidates/${candidateId}`,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const useDeleteAnonymizeCandidate = () => {
  const [state, execute, cancel] = apiAxios(
    {
      url: "/candidates",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    ({ candidateId, isDeleted }) => {
      return execute({
        url: `/candidates/${candidateId}?isDeleted=${!!isDeleted}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

/**
 * Route API V2
 */

const axios = buildMakeUseAxios(`${import.meta.env.VITE_CANDIDATE_SERVICE_URL}/v2`);

export const useCreateOnboardingCandidate = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/onboarding",
      method: "POST",
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: unknown) => {
      return execute({
        url: "/candidates/onboarding",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePostBatchCandidates = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/batch",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: unknown) => {
      return execute({
        url: "/candidates/batch",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePostBatchVerifyCandidates = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/verify-many",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: unknown) => {
      return execute({
        url: "/candidates/verify-many",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

// TODO refaire le type -> type refait (à confirmer)
export interface ICandidatesSearchFilters {
  skip?: number;
  limit?: number;
  isAnonymized?: boolean;
  isDeleted?: boolean;
  isArchived?: boolean;
  sort?: {
    sortDeadlineDate?: 1;
    sortExecutionDate?: 1;
    lastName: 1;
    firstName: 1;
  };
  multiSearch?: string;
  authors?: ObjectId[];
  status?: string[];
  coachingSteps?: string[];
  source?: string[];
  place?: string[];
  additionalSections?: AdditionalFiltersFormatted;
}

export interface IUsePostCandidatesSearchBody {
  limit: number;
  skip: number;
  count: number;
  total: number;
  data: { _id: ObjectId; firstName: string; lastName: string }[];
}

export const usePostCandidatesSearch = () => {
  const [state, execute, cancel] = axios<IUsePostCandidatesSearchBody>(
    {
      url: "/candidates/search",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (filters: ICandidatesSearchFilters) => {
      return execute({
        url: "/candidates/search",
        data: filters,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

interface IUsePostCandidatesOfferSearchBody {
  data: ICandidate[];
  limit: number;
  skip: number;
  total: number;
  count: number;
}

export const usePostCandidatesOfferSearch = () => {
  const [state, execute, cancel] = axios<IUsePostCandidatesOfferSearchBody>(
    {
      url: "/candidates/offer/search",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (filters: ICandidatesFiltersBody) => {
      return execute({
        url: "/candidates/offer/search",
        data: filters,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const usePostApplicationFile = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/:id/application-files",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId, data: unknown) => {
      return execute({
        url: `/candidates/${candidateId}/application-files`,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePutApplicationFile = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/:id/application-files",
      method: "PUT",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId, data: unknown) => {
      return execute({
        url: `/candidates/${candidateId}/application-files`,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetCandidateApplicationFiles = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/:id/application-files",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId) => {
      return execute({
        url: `/candidates/${candidateId}/application-files`,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const useDeleteCandidateApplicationFiles = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/:id/application-files",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId, applicationId) => {
      return execute({
        url: `/candidates/${candidateId}/application-files/${applicationId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetCandidateMetabaseUrl = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/:id/metabase-url",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId) => {
      return execute({
        url: `/candidates/${candidateId}/metabase-url`,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

interface IUpdateStatusBody {
  oldStatus?: string;
  candidates?: string[];
  newStatus: string;
}

export const useTransferCandidateStatus = () => {
  const [state, execute, cancel] = axios<void, IUpdateStatusBody, unknown>(
    {
      url: "/candidates/transferStatus",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IUpdateStatusBody) => {
      return execute({
        url: "/candidates/transferStatus",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface ISettingStepWithIDs {
  candidateIds: string[];
  newStep: string;
}

interface ISettingStepWithType {
  oldStep: string;
  newStep: string;
}

export const useTransferCandidateCoachingStep = () => {
  const [state, execute, cancel] = axios<void, ISettingStepWithIDs | ISettingStepWithType, unknown>(
    {
      url: "candidates/coachingStep",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: ISettingStepWithIDs | ISettingStepWithType) => {
      return execute({
        url: "candidates/coachingStep",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IPatchBatchCandidatesBody {
  query?: IGetDashboardFilters;
  candidateIds?: string[];
  field: string;
  value?: string | number | boolean | string[] | Date | { replace: ObjectId[]; newAuthors: ObjectId[] };
}

export const usePatchBatchCandidates = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/candidates/batch",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (batchCandidate: IPatchBatchCandidatesBody) => {
      return execute({
        data: batchCandidate,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IPostModifiedFieldCountBody {
  query?: IGetDashboardFilters;
  candidateIds?: string[];
  field: string;
  value?: string | string[] | number | boolean | Date;
}

export const usePostModifiedFieldCount = () => {
  const [state, execute, cancel] = axios<number, IPostModifiedFieldCountBody, unknown>(
    {
      url: "/candidates/modifiedField/count",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (countBody: IPostModifiedFieldCountBody) => {
      return execute({
        data: countBody,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export * from "./cer";
export * from "./coachingSummary";
export * from "./create";
export * from "./createFromPrescriber";
export type * from "./dashboard";
export * from "./dashboard";
export * from "./filterViews";
export * from "./update";
export * from "./transitions";
export * from "./searchCandidates";
export * from "./exist";
export * from "./eptRenewal";
export * from "./sendEmails";
export * from "./postAllCandidatesDashboard";
export * from "./sendSMS";
export * from "./getCerSuspense";
export * from "./getCandidateMailPreview";
export * from "./getSanctionConsequence";
export * from "./getSanctionListAll";
export * from "./getSanctionListAllSuspense";
export * from "./getSanctionListByCandidate";
export * from "./postCandidateSendMail";
export * from "./post";
export * from "./verifyPhoneNumbers";
